import React, {FC, memo, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {isDefined} from '../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {
  buildContactDisplayHandle,
  buildContactDisplayName,
} from '../../../../../helpers/models/contactHelpers';
import {ApplicationChannelTypeModel} from '../../../../../models/api/applicationChannelTypeModel';
import {HighlightModel} from '../../../../../models/api/conversationHighlightModel';
import {ExternalChannelTypeModel} from '../../../../../models/api/externalChannelTypeModel';
import {MessageTypesEnum} from '../../../../../models/api/messageModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {MutationProofText} from '../../../../common/text/mutationProofText';
import {Highlight} from '../../header/search/highlight/highlight';
import {CellRating} from './cellRating';
import {CellStarIcon} from './cellStarIcon';
import {CellTags} from './cellTags';

/*
 * Props.
 */

interface CellMiddleProps {
  messageType: MessageTypesEnum;
  subject: string;
  subjectHighlight?: HighlightModel;
  recipient?: VisualRecipientModel;
  tags: ReadonlyArray<TagModel>;
  badgeValue: number;
  isSelected?: boolean;
  isStarred?: boolean;
  externalChannelType?: ExternalChannelTypeModel;
  applicationChannelType?: ApplicationChannelTypeModel;
  hasP2QuickActions?: boolean;
}

/*
 * Style.
 */

const StyledFlexDiv = styled.div`
  grid-area: middle-line;
  display: flex;
  min-width: 0;
`;

const StyledGridDiv = styled.div`
  flex: 1;

  display: grid;
  grid-template-columns: auto;
  grid-auto-columns: minmax(auto, max-content);
  grid-auto-flow: column;
`;

interface CellMiddleStyleProps {
  $isSelected?: boolean;
  $hasHighlight?: boolean;
}
const StyledSubjectDiv = styled.div<CellMiddleStyleProps>`
  /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
  color: ${(p) => (p.$isSelected && !p.$hasHighlight ? p.theme.invertedText : p.theme.greys.shade90)};

  padding: 3px;
  margin: -3px;
  ${ellipsis()};
`;

interface BadgeStyleProps extends CellMiddleStyleProps {
  $badgeValue: number;
  $hasP2QuickActions: boolean;
}

const StyledBadgeDiv = styled.div<BadgeStyleProps>`
  flex-shrink: 0;
  min-width: 16px;
  margin-left: ${(p) => (p.$hasP2QuickActions ? '8px' : '4px')};
  padding: 0 4px;
  border-radius: 8px;

  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.greys.shade70)};
  ${(p) => addBadgeBackgroundStyles(p)};

  line-height: 16px;
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};

  ${center()};
`;

function addBadgeBackgroundStyles(props: BadgeStyleProps) {
  if (props.$badgeValue <= 1) {
    return css`
      background-color: transparent;
    `;
  }

  return css`
    background-color: ${(p) => (props.$isSelected ? p.theme.alphas.white30 : p.theme.greys.shade30)};
  `;
}

/*
 * Component.
 */

export const CellMiddle: FC<CellMiddleProps> = memo((props) => (
  <StyledFlexDiv>
    <StyledGridDiv>
      {renderSubject(props)}
      <CellTags tags={props.tags} />
      {props.isStarred && <CellStarIcon />}
    </StyledGridDiv>
    {
      <StyledBadgeDiv
        $isSelected={props.isSelected}
        $badgeValue={props.badgeValue}
        $hasP2QuickActions={Boolean(props.hasP2QuickActions)}
      >
        {props.badgeValue > 1 && props.badgeValue}
      </StyledBadgeDiv>
    }
  </StyledFlexDiv>
));

/*
 * Channel-specific logic.
 */

function renderSubject(props: CellMiddleProps) {
  const {messageType, subject, isSelected} = props;
  switch (messageType) {
    case MessageTypesEnum.GOOGLE_PLAY:
      return <CellRating rating={subject} isSelected={isSelected} />;

    default:
      return (
        <StyledSubjectDiv $isSelected={props.isSelected} $hasHighlight={isDefined(props.subjectHighlight)}>
          {renderCellSubjectContent(props)}
        </StyledSubjectDiv>
      );
  }
}

export function renderCellSubjectContent(props: CellMiddleProps) {
  const {
    messageType,
    subject,
    subjectHighlight,
    recipient,
    externalChannelType,
    applicationChannelType,
    isSelected,
  } = props;
  switch (messageType) {
    // Some conversations have a subject.
    case MessageTypesEnum.EMAIL:
    case MessageTypesEnum.INTERNAL:
      return renderTextSubject(subject, messageType, isSelected, subjectHighlight);

    // Custom messages may use externalChannelType or applicationChannelType for the name.
    case MessageTypesEnum.CUSTOM:
      return renderTextSubject(
        subject,
        messageType,
        isSelected,
        subjectHighlight,
        externalChannelType,
        applicationChannelType,
      );

    // Others have human-readable handles.
    case MessageTypesEnum.TWEET:
    case MessageTypesEnum.TWEET_DM:
    case MessageTypesEnum.CALL:
    case MessageTypesEnum.SMS:
      return maybeRenderHandleSubject(messageType, recipient);

    // Otherwise, always show the placeholder.
    default:
      return renderPlaceholder(messageType);
  }
}

export function renderTextSubject(
  subject: string,
  messageType: MessageTypesEnum,
  isSelected?: boolean,
  highlight?: HighlightModel,
  externalChannelType?: ExternalChannelTypeModel,
  applicationChannelType?: ApplicationChannelTypeModel,
) {
  // If we have a highlight, use it.
  if (highlight) {
    return (
      <Highlight {...highlight} backgroundColor={PaletteColorsEnum.YELLOW} isParentSelected={isSelected} />
    );
  }

  // If we have a subject, render that.
  const displaySubject = subject.trim();
  if (displaySubject) {
    return <MutationProofText>{displaySubject}</MutationProofText>;
  }

  // If we have a application channel type, render that instead of placeholder.
  if (applicationChannelType) {
    return applicationChannelType.name;
  }

  // If we have a external channel type, render that instead of placeholder.
  if (externalChannelType) {
    return externalChannelType.name;
  }

  // Otherwise, render a placeholder.
  return renderPlaceholder(messageType);
}

function maybeRenderHandleSubject(messageType: MessageTypesEnum, recipient?: VisualRecipientModel) {
  // A recipient is needed at this point.
  if (!recipient) {
    return renderPlaceholder(messageType);
  }

  const name = buildContactDisplayName(recipient);
  const handle = buildContactDisplayHandle(recipient);

  // If the name and handle are different, render the handle as subject.
  if (name !== handle) {
    return handle;
  }

  // Otherwise, render the placeholder.
  return renderPlaceholder(messageType);
}

function renderPlaceholder(messageType: MessageTypesEnum) {
  return <em>{placeholderSubjectsByMessageType[messageType]}</em>;
}

type PlaceholderRenderers = {[T in MessageTypesEnum]: ReactNode};
// prettier-ignore
export const placeholderSubjectsByMessageType: PlaceholderRenderers = {
  [MessageTypesEnum.INTERNAL]: <Trans id="cell-placeholder-internal">No topic</Trans>,
  [MessageTypesEnum.EMAIL]: <Trans id="cell-placeholder-email">No subject</Trans>,
  [MessageTypesEnum.TWEET]: <Trans id="cell-placeholder-twitter">Twitter</Trans>,
  [MessageTypesEnum.TWEET_DM]: <Trans id="cell-placeholder-twitterdm">Twitter DM</Trans>,
  [MessageTypesEnum.CALL]: <Trans id="cell-placeholder-call">Phone call</Trans>,
  [MessageTypesEnum.SMS]: <Trans id="cell-placeholder-sms">SMS</Trans>,
  [MessageTypesEnum.GOOGLE_PLAY]: <Trans id="cell-placeholder-googleplay">Google Play Review</Trans>,
  [MessageTypesEnum.FACEBOOK]: <Trans id="cell-placeholder-facebook">Facebook</Trans>,
  [MessageTypesEnum.INTERCOM]: <Trans id="cell-placeholder-intercom">Intercom</Trans>,
  [MessageTypesEnum.SMOOCH]: <Trans id="cell-placeholder-smooch">Smooch</Trans>,
  [MessageTypesEnum.LAYER]: <Trans id="cell-placeholder-chat">Chat</Trans>,
  [MessageTypesEnum.CUSTOM]: <Trans id="cell-placeholder-custom">Custom</Trans>,
  [MessageTypesEnum.YALO_WHATSAPP]: <Trans id="cell-placeholder-whatsapp">WhatsApp</Trans>,
  [MessageTypesEnum.WHATSAPP]: <Trans id="cell-placeholder-whatsapp">WhatsApp</Trans>,
  [MessageTypesEnum.FRONT_CHAT]: <Trans id="cell-placeholder-frontchat">Chat</Trans>
};
